define((require) => {
    "use strict";

    const {defer}                  = require("underscore");
    const EmailUtil                = require("util/EmailUtil");
    const ImprovedView             = require("editor/zbone/ImprovedView");
    const Login                    = require("security/Login");
    const PasswordQualityValidator = require("util/PasswordQualityValidator");
    const PasswordQualityView      = require("ui/user/PasswordQualityView");
    const SequentialButton         = require("util/SequentialButton");
    const {isBlank}                = require("shared/util/StringUtils");
    const template                 = require("ui/user/ResetPasswordView.tpl");
    const ThrottleView             = require("ui/common/ThrottleView");
    const Tooltip                  = require("ui/common/Tooltip");
    const ValidateBehavior         = require("ui/behaviors/ValidateBehavior");

    const SHAKE_EFFECT_CLASS_NAME = "shake";

    /**
     *
     * @constructor
     *
     * @cfg {String}  resetToken    The reset token.
     * @cfg {Promise} loginDeferred The promise to resolve with logged in princiapl on successful reset.
     */
    return ImprovedView.extend({

        /**
         * @private @type {SequentialButton}
         * The sequential button guarding (throttled) access submitting reset password requests.
         */
        _action: null,
        /**
         * @private @type {ThrottleView}
         * The throttle view handling lockout and displaying lockout messages.
         */
        _throttleView: null,
        /**
         * @private
         * @type {Promise}
         * The promise to resolve on successful reset / sign in.
         */
        _loginDeferred: null,

        template,

        className: "reset-password-page pt-page",

        regions: {
            throttleRegion: ".throttle-region",
            defaultFooterRegion: ".default-footer"
        },

        ui: {
            emailInput:     ".email-input",
            passwordInput:  ".password-input",
            persistent:     ".persistent-input",
            resetButton:    "button",
            innerContainer: ".inner-container"
        },

        events: {
            "submit form": "_formSubmitHandler"
        },

        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                buttonSubmit: "button",
                inputValidation: {
                    ".email-input": {
                        validator(email) {
                            return EmailUtil.validateEmail(email);
                        },
                        errorText(email) {
                            if (this.ui.passwordInput.is(":focus")) {
                                return "";
                            }
                            return email ? "Not a valid email" : "Email required";
                        }
                    },
                    ".password-input": {
                        validator(password) {
                            return PasswordQualityValidator.isValid(password);
                        },
                        errorText(password) {
                            if (this.ui.passwordInput.is(":focus")) {
                                return "";
                            }
                            return password ? "Password is not secure" : "Password required";
                        }
                    },
                    ".retype-password-input": {
                        validator(password) {
                            return !isBlank(password) && password === this.ui.passwordInput.val(); // passwords must match!
                        },
                        errorText(password) {
                            if (this.ui.passwordInput.is(":focus")) {
                                return "";
                            }
                            return !isBlank(password) ? "Passwords do not match" : "Password confirmation required";
                        }
                    }
                }
            }
        },

        transitions: {
            "in": "pt-page-scaleUp",
            out: "pt-page-scaleDown"
        },

        _formSubmitHandler(event) {
            // Stop form submitting normally...
            event.preventDefault();
            this._action();
            return false;
        },

        _reset() {
            // Update the display of validation errors and break out if the form is invalid...
            if (!this.inputValidation()) {
                return false;
            }

            const {
                innerContainer, emailInput, passwordInput, persistent
            } = this.ui;

            innerContainer.removeClass(SHAKE_EFFECT_CLASS_NAME);

            // Reset password...
            const promise = Login.resetPassword(this.options.resetToken, emailInput.val(), passwordInput.val(), persistent.val())
                .done((principal) => {
                    this._loginDeferred.resolve(principal);
                })
                .fail(() => {
                    innerContainer.addClass(SHAKE_EFFECT_CLASS_NAME);
                });

            // Check the response for throttling status code...
            return this._throttleView.checkPromise(emailInput.val(), promise);
        },

        initialize() {
            this._loginDeferred = this.options.loginDeferred;
        },

        onRender() {
            const {resetButton, passwordInput, emailInput} = this.ui;

            this._action = SequentialButton.add(resetButton, this._reset.bind(this), true, true);

            // Add tooltip for password quality. We do a manual trigger to ensure we only bother the user with the
            // tooltip while writing and while invalid...
            const tooltip = Tooltip.add(passwordInput, {
                width:     230,
                height:    210,
                placement: "right",
                trigger:   "manual",
                offset:    {align: "right"},
                delay:     {hide: 0},
                html:      true,
                duration:  -1,
                classes:   "password-quality-tooltip",
                title:     false, // to avoid conflict with input validation that uses the title...
                content:   new PasswordQualityView({element: passwordInput})
            });
            let shown = false;

            // Show password quality tooltip on focus or key up if and only if the password is valid...
            passwordInput.on("focus keyup", () => {
                if (!shown && !PasswordQualityValidator.isValid(passwordInput.val())) {
                    Tooltip.show(tooltip);
                    shown = true;
                }
            });

            // Hide password quality tooltip on blur...
            passwordInput.on("blur", () => {
                Tooltip.hide(tooltip);
                shown = false;
            });

            // Throttle view...
            this._throttleView = new ThrottleView({
                name:     "forgot",
                target:   emailInput,              // entered email = target
                validate: EmailUtil.validateEmail, // make sure we only throttle for valid emails, will not submit otherwise
                button:   this._action             // disable button during lockout
            });
            this.throttleRegion.show(this._throttleView);

            // Need to defer in order for dom to be ready
            defer(() => {
                emailInput.focus();
            });
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/user/ResetPasswordView.js